import { PageHeaderLink } from "@/components/PageHeaderLink";
import { Shell } from "@/components/Shell";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@/components/ui/empty";
import {
	Field,
	FieldError,
	FieldGroup,
	FieldLabel,
	FieldSet,
} from "@/components/ui/field";
import { useForm } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupInput,
	InputGroupText,
} from "@/components/ui/input-group";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { formatRelativeTime } from "@/lib/utils";
import {
	useMutation,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import {
	CodeIcon,
	CopyIcon,
	FolderCodeIcon,
	GlobeIcon,
	RotateCcwIcon,
	SettingsIcon,
} from "lucide-react";
import { Fragment, Suspense, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

interface App {
	id: string;
	name: string;
	website: string | null;
	public: boolean;
}

interface Service {
	id: string;
	name: string;
	url: string;
	endpoint: string;
	lastCheckedAt: string | null;
	lastStatus: string | null;
}

async function fetchApp(appId: string): Promise<App | null> {
	const res = await fetch(`/api/applications/${appId}`, {
		credentials: "include",
	});
	if (res.status === 404) return null;
	if (!res.ok) throw new Error("Failed to load application");
	return res.json() as Promise<App>;
}

async function fetchServices(appId: string): Promise<Service[]> {
	const res = await fetch(`/api/applications/${appId}/services`, {
		credentials: "include",
	});
	if (!res.ok) throw new Error("Failed to load services");
	const data = (await res.json()) as { services: Service[] };
	return data.services ?? [];
}

const schema = z.object({
	name: z
		.string("Name is required")
		.min(1, "Name is required")
		.max(255, "Name is too long"),
	website: z
		.string()
		.regex(
			/^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/,
			"URL is invalid",
		)
		.or(z.literal(""))
		.optional(),
	public: z.boolean(),
});

const LoadingState = () => {
	return (
		<Fragment>
			<Skeleton className="h-5 w-32" />
			<Skeleton className="h-64 w-full" />
			<Skeleton className="h-48 w-full" />
		</Fragment>
	);
};

const EmptyState = () => {
	return (
		<Fragment>
			<PageHeaderLink label="Dashboard" />
			<Empty>
				<EmptyHeader>
					<EmptyMedia variant="icon">
						<FolderCodeIcon />
					</EmptyMedia>
					<EmptyTitle>Application not found</EmptyTitle>
					<EmptyDescription>
						This application may have been deleted or you may not have access.
					</EmptyDescription>
				</EmptyHeader>
			</Empty>
		</Fragment>
	);
};

const ServicesEmptyState = () => {
	return (
		<p className="text-muted-foreground text-sm py-4">
			No services yet. Add one to start monitoring.
		</p>
	);
};

const Content = ({ appId }: { appId: string }) => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const appQuery = useSuspenseQuery({
		queryKey: ["application", appId],
		queryFn: () => fetchApp(appId),
	});

	const servicesQuery = useSuspenseQuery({
		queryKey: ["services", appId],
		queryFn: () => fetchServices(appId),
	});

	const queryError = appQuery.error ?? servicesQuery.error;
	const app = appQuery.data;
	const services = servicesQuery.data ?? [];

	const updateApp = useMutation({
		mutationFn: (body: {
			name: string;
			website: string | null;
			public: boolean;
		}) =>
			fetch(`/api/applications/${appId}`, {
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
				body: JSON.stringify(body),
			}).then(async (res) => {
				const data = (await res.json().catch(() => null)) as {
					error?: string;
				} | null;
				if (!res.ok) {
					throw new Error(data?.error ?? "Failed to update application");
				}
				return data;
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["application", appId] });
			queryClient.invalidateQueries({ queryKey: ["applications"] });
		},
	});

	const deleteApp = useMutation({
		mutationFn: () =>
			fetch(`/api/applications/${appId}`, {
				method: "DELETE",
				credentials: "include",
			}),
		onSuccess: (res) => {
			if (res.ok) {
				queryClient.invalidateQueries({ queryKey: ["applications"] });
				navigate("/", { replace: true });
			}
		},
	});

	const triggerCheck = useMutation({
		mutationFn: (serviceId: string) =>
			fetch(`/api/applications/${appId}/services/${serviceId}/check`, {
				method: "POST",
				credentials: "include",
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["services", appId] });
		},
	});

	const form = useForm<z.infer<typeof schema>>({
		validators: {
			onSubmit: schema,
		},
		defaultValues: {
			name: "",
			website: undefined,
			public: false,
		},
		onSubmit: async ({ value }) => {
			await updateApp.mutateAsync({
				name: value.name.trim(),
				website: value.website?.trim() ? value.website.trim() : null,
				public: value.public,
			});
		},
	});

	useEffect(() => {
		if (!app) return;
		form.reset({
			name: app.name ?? "",
			website: app.website?.split("//")[1] ?? undefined,
			public: app.public,
		});
	}, [app, form]);

	if (queryError) {
		return <div className="text-destructive">{queryError.message}</div>;
	}
	if (!app) return <EmptyState />;

	const iframeUrl =
		typeof window !== "undefined"
			? `${window.location.origin}/public/${appId}`
			: "";

	return (
		<Fragment>
			<PageHeaderLink label="Dashboard" />
			<Card>
				<CardHeader>
					<CardTitle>Application settings</CardTitle>
					<CardDescription>
						Name, website, and visibility for this application.
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<form
						id="app-config"
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							form.handleSubmit();
						}}
					>
						{updateApp.error && (
							<p
								className="text-sm text-destructive bg-destructive/10 p-2 rounded"
								role="alert"
							>
								{updateApp.error.message}
							</p>
						)}
						<FieldGroup>
							<FieldSet>
								<form.Field name="name">
									{(field) => (
										<Field>
											<FieldLabel htmlFor="app-name">Name</FieldLabel>
											<Input
												id="app-name"
												value={field.state.value}
												onChange={(e) => field.handleChange(e.target.value)}
												onBlur={field.handleBlur}
												disabled={updateApp.isPending}
											/>
											<FieldError errors={field.state.meta.errors} />
										</Field>
									)}
								</form.Field>
								<form.Field name="website">
									{(field) => (
										<Field>
											<FieldLabel htmlFor="website">Website</FieldLabel>
											<InputGroup>
												<InputGroupAddon>
													<GlobeIcon />
													<InputGroupText>https://</InputGroupText>
												</InputGroupAddon>
												<InputGroupInput
													id="website"
													value={field.state.value ?? ""}
													onChange={(e) => field.handleChange(e.target.value)}
													onBlur={field.handleBlur}
													placeholder="example.com"
													className="pl-0!"
													disabled={updateApp.isPending}
												/>
											</InputGroup>
											<FieldError errors={field.state.meta.errors} />
										</Field>
									)}
								</form.Field>
								<form.Field name="public">
									{(field) => (
										<div className="flex items-center gap-2">
											<Checkbox
												id="app-public"
												checked={field.state.value}
												onCheckedChange={(checked) =>
													field.handleChange(checked === true)
												}
												disabled={updateApp.isPending}
											/>
											<Label htmlFor="app-public">Public status page</Label>
										</div>
									)}
								</form.Field>
								<form.Subscribe selector={(state) => state.values.public}>
									{(isPublic) =>
										isPublic && (
											<Field>
												<FieldLabel htmlFor="embed">Embed snippet</FieldLabel>
												<InputGroup>
													<InputGroupAddon>
														<CodeIcon />
													</InputGroupAddon>
													<InputGroupInput
														id="embed"
														readOnly
														value={`<iframe src="${iframeUrl}" width="400" height="200" frameborder="0"></iframe>`}
													/>
													<InputGroupAddon align="inline-end">
														<InputGroupButton
															type="button"
															variant="ghost"
															size="icon-xs"
															onClick={() => {
																navigator.clipboard.writeText(
																	`<iframe src="${iframeUrl}" width="400" height="200" frameborder="0"></iframe>`,
																);
																toast.success("Copied to clipboard");
															}}
														>
															<CopyIcon />
														</InputGroupButton>
													</InputGroupAddon>
												</InputGroup>
											</Field>
										)
									}
								</form.Subscribe>
							</FieldSet>
						</FieldGroup>
					</form>
				</CardContent>
				<CardFooter className="grid">
					<form.Subscribe
						selector={(state) => [
							state.isSubmitting,
							state.isValid,
							state.isDirty,
						]}
					>
						{([isSubmitting, isValid, isDirty]) => (
							<Button
								type="submit"
								form="app-config"
								disabled={!isValid || !isDirty}
								loading={isSubmitting || updateApp.isPending}
							>
								Save
							</Button>
						)}
					</form.Subscribe>
				</CardFooter>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Services</CardTitle>
					<CardDescription>
						Monitored endpoints for this application.
					</CardDescription>
				</CardHeader>
				<CardContent>
					{services.length === 0 ? (
						<ServicesEmptyState />
					) : (
						<ul className="space-y-1">
							{services.map((s) => (
								<li
									key={s.id}
									className="flex flex-wrap items-center justify-between gap-2 py-3 px-3 -mx-3 rounded-lg hover:bg-muted/50 transition-colors"
								>
									<div className="flex flex-col gap-1">
										<Link
											to={`/${appId}/${s.id}`}
											className="font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-ring rounded"
										>
											{s.name}
										</Link>
										<div className="flex flex-wrap items-center gap-2">
											<StatusBadge status={s.lastStatus} />
											{s.lastCheckedAt && (
												<span className="text-muted-foreground text-xs shrink-0">
													{formatRelativeTime(s.lastCheckedAt)}
												</span>
											)}
										</div>
									</div>
									<div className="flex gap-2 shrink-0">
										<Button
											variant="ghost"
											size="icon"
											onClick={() => triggerCheck.mutate(s.id)}
											disabled={triggerCheck.isPending}
											aria-label="Check now"
											title="Check now"
										>
											<RotateCcwIcon className="size-4" />
										</Button>
										<Button
											variant="outline"
											size="icon"
											asChild
											aria-label="Service settings"
											title="Service settings"
										>
											<Link to={`/${appId}/${s.id}`}>
												<SettingsIcon className="size-4" />
											</Link>
										</Button>
									</div>
								</li>
							))}
						</ul>
					)}
				</CardContent>
				<CardFooter className="grid">
					<Button asChild variant="outline">
						<Link to={`/${appId}/new-service`}>Add service</Link>
					</Button>
				</CardFooter>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Danger zone</CardTitle>
					<CardDescription>
						Delete this application and all of its services.
					</CardDescription>
				</CardHeader>
				<CardFooter className="grid">
					<Button
						size="sm"
						variant="outline"
						onClick={() => {
							if (confirm("Delete this application and all its services?"))
								deleteApp.mutate();
						}}
						disabled={deleteApp.isPending}
						className="border-destructive text-destructive hover:text-destructive-foreground hover:bg-destructive/10"
					>
						Delete application
					</Button>
				</CardFooter>
			</Card>
		</Fragment>
	);
};

export function AppConfig() {
	const { appId } = useParams<{ appId: string }>();

	return (
		<Shell className="flex flex-col gap-4">
			<Suspense fallback={<LoadingState />}>
				{appId ? <Content appId={appId} /> : <EmptyState />}
			</Suspense>
		</Shell>
	);
}
